import React, { useState, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import Table, { TableRow, TableCell } from '@amzn/meridian/table';
import { TableSortDirection } from '@amzn/meridian/table/table';
import Button from '@amzn/meridian/button';
import Pagination from '@amzn/meridian/pagination';
import Select, { SelectOption } from '@amzn/meridian/select';
import Text from '@amzn/meridian/text';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import { OMOutlierSummaryStats } from '../../types/types';
import {
  DEFAULT_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
  NUM_PAGES_BEFORE_SKIP_ARROW,
  MIN_NUM_PAGES_FOR_PAGINATION,
  ENABLE_SEND_REMINDER_BUTTON,
} from '../../types/constants';
import useInspireOptions from '../../../../hooks/useInspireOptions';

type OnSendReminderCallback = (
  tmIdWithCSCPrefix: string,
  tmDisplayName: string,
  tmUsername: string,
  omIdWithCSCPrefix: string,
  outlierCount: number
) => Promise<boolean>;

interface OMOutliersTableProps {
  data: OMOutlierSummaryStats[];
  sortColumn: string;
  sortDirection: TableSortDirection;
  onSort: (params: {
    sortColumn: string;
    sortDirection: TableSortDirection;
  }) => void;
  onSendReminder: OnSendReminderCallback;
}

export const OMOutliersTable: React.FC<OMOutliersTableProps> = ({
  data,
  sortColumn,
  sortDirection,
  onSort,
  onSendReminder,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => {
      switch (sortColumn) {
        case 'tmAlias':
          return sortDirection === 'ascending'
            ? a.tmAlias.localeCompare(b.tmAlias)
            : b.tmAlias.localeCompare(a.tmAlias);
        case 'totalOutliers':
          return sortDirection === 'ascending'
            ? a.totalOutliers - b.totalOutliers
            : b.totalOutliers - a.totalOutliers;
        case 'pendingOutliers':
          return sortDirection === 'ascending'
            ? a.pendingOutliers - b.pendingOutliers
            : b.pendingOutliers - a.pendingOutliers;
        case 'totalRequired':
          return sortDirection === 'ascending'
            ? a.totalRequired - b.totalRequired
            : b.totalRequired - a.totalRequired;
        case 'requiredRemaining':
          return sortDirection === 'ascending'
            ? a.requiredRemaining - b.requiredRemaining
            : b.requiredRemaining - a.requiredRemaining;
        default:
          return 0;
      }
    });
  }, [data, sortColumn, sortDirection]);

  const { paginatedData, totalPages } = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const paginatedData = sortedData.slice(startIndex, startIndex + pageSize);
    const totalPages = Math.ceil(data.length / pageSize);
    return { paginatedData, totalPages };
  }, [sortedData, currentPage, pageSize, data.length]);

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  const handleSort = (params: {
    sortColumn: string;
    sortDirection: TableSortDirection;
  }) => {
    setCurrentPage(1);
    onSort(params);
  };

  return (
    <Column spacing='medium'>
      <Table
        headerRows={1}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSort={handleSort}
        showDividers={true}
        showStripes={true}
        spacing='small'
      >
        <TableRow>
          <TableCell sortColumn='tmAlias' width={200}>
            <FormattedMessage id='cs_perfportal_tm_alias' defaultMessage='TM' />
          </TableCell>
          <TableCell sortColumn='totalOutliers' width={200}>
            <FormattedMessage
              id='cs_perfportal_total_outliers'
              defaultMessage='Total'
            />
          </TableCell>
          <TableCell sortColumn='requiredRemaining' width={200}>
            <FormattedMessage
              id='cs_perfportal_outlier_remaining_reviews_required'
              defaultMessage='Remaining reviews required'
            />
          </TableCell>
          <TableCell sortColumn='totalRequired' width={200}>
            <FormattedMessage
              id='cs_perfportal_outlier_required_total'
              defaultMessage='Total reviews required'
            />
          </TableCell>
          {ENABLE_SEND_REMINDER_BUTTON() && (
            <TableCell>
              <FormattedMessage
                id='cs_perfportal_actions'
                defaultMessage='Actions'
              />
            </TableCell>
          )}
        </TableRow>
        {paginatedData.map(tm => (
          <TableRow key={tm.tmAlias}>
            <TableCell>{tm.tmAlias}</TableCell>
            <TableCell>{tm.totalOutliers}</TableCell>
            <TableCell>{tm.requiredRemaining}</TableCell>
            <TableCell>{tm.totalRequired}</TableCell>
            {ENABLE_SEND_REMINDER_BUTTON() && (
              <TableCell>
                <SendReminderButton onSendReminder={onSendReminder} tm={tm} />
              </TableCell>
            )}
          </TableRow>
        ))}
      </Table>

      <Row
        alignmentHorizontal='justify'
        alignmentVertical='center'
        spacingInset='300 none'
      >
        <Row
          spacing='small'
          alignmentVertical='center'
          spacingInset='none none none 500'
        >
          <Text type='b200'>
            <FormattedMessage
              id='cs_perfportal_results_per_page'
              defaultMessage='Results per page'
            />
          </Text>
          <Row spacingInset='none 300 none 300'>
            <Select
              value={pageSize}
              onChange={handlePageSizeChange}
              width={75}
              size='small'
            >
              {PAGE_SIZE_OPTIONS.map(size => (
                <SelectOption key={size} value={size} label={size.toString()} />
              ))}
            </Select>
          </Row>
        </Row>

        {totalPages > MIN_NUM_PAGES_FOR_PAGINATION && (
          <Pagination
            numberOfPages={totalPages}
            currentPage={currentPage}
            onChange={setCurrentPage}
            showSkipArrows={totalPages > NUM_PAGES_BEFORE_SKIP_ARROW}
          />
        )}
      </Row>
    </Column>
  );
};

interface SendReminderButtonProps {
  onSendReminder: OnSendReminderCallback;
  tm: OMOutlierSummaryStats;
}

const SendReminderButton = ({
  tm,
  onSendReminder,
}: SendReminderButtonProps) => {
  const { isTm, isOm, requesterUserID, requesterUsername, cscEntityPrefix } =
    useInspireOptions();

  const [disabled, setDisabled] = useState(
    tm.tmDisplayName && tm.tmId ? false : true
  );

  const handleSendReminder = async () => {
    if (tm.tmDisplayName && tm.tmId) {
      const didSucceed = await onSendReminder(
        `${cscEntityPrefix}:${tm.tmId}`,
        requesterUserID,
        tm.tmDisplayName,
        tm.tmAlias,
        tm.requiredRemaining
      );

      setDisabled(didSucceed);
    }
  };

  return (
    <Button
      size='small'
      onClick={() => handleSendReminder()}
      disabled={disabled}
      type='primary'
      data-csa-c-type='button'
      data-csa-c-slot-id='om-outlier-management-outlier-send-reminder-button-slot'
      data-csa-c-tag1='Coaching'
      data-csa-c-tag2='Outlier Metrics'
      data-csa-c-tag3={tm.tmAlias}
      data-csa-c-profile-id={requesterUserID}
      data-csa-c-username={requesterUsername}
      data-csa-c-is-TM={isTm}
      data-csa-c-is-OM={isOm}
    >
      <FormattedMessage
        id='cs_perfportal_outlier_send_reminder'
        defaultMessage='Send reminder'
      />
    </Button>
  );
};

export default OMOutliersTable;
